import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { CalendarTimeEntry, CalendarMonthData } from '../../services/types';
import { getCalendarMonth } from '../../services/api';
import {
  formatHours,
  getCompanyColor,
  normalizeCompanyName,
  getTotalHours,
  formatShortDate,
  shortMonthNames,
  formatDateKey,
} from './calendarUtils';

interface ScheduleViewProps {
  currentDate: Date;
  selectedResourceId: number | null;
  monthData: CalendarMonthData | null;
  selectedCompanies: Set<string>;
  searchFilter?: (entry: CalendarTimeEntry) => boolean;
  onEntryClick: (entry: CalendarTimeEntry, event: React.MouseEvent) => void;
  onAddEntry: (date: Date) => void;
}

interface DayGroup {
  date: Date;
  dateStr: string;
  entries: CalendarTimeEntry[];
  totalHours: number;
}

interface MonthCache {
  [key: string]: CalendarMonthData | null;
}

export default function ScheduleView({
  currentDate,
  selectedResourceId,
  monthData,
  selectedCompanies,
  searchFilter,
  onEntryClick,
  onAddEntry,
}: ScheduleViewProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [visibleDateRange, setVisibleDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

  // Cache for additional months
  const [monthCache, setMonthCache] = useState<MonthCache>({});
  const [loadingMonths, setLoadingMonths] = useState<Set<string>>(new Set());

  // Get cache key for a date
  const getCacheKey = (year: number, month: number) => `${year}-${month + 1}`;

  // Load additional month data
  const loadMonth = useCallback(async (year: number, month: number) => {
    if (!selectedResourceId) return;

    const key = getCacheKey(year, month);
    if (monthCache[key] !== undefined || loadingMonths.has(key)) return;

    setLoadingMonths(prev => new Set([...prev, key]));
    try {
      const data = await getCalendarMonth(year, month + 1, selectedResourceId);
      setMonthCache(prev => ({ ...prev, [key]: data }));
    } catch {
      setMonthCache(prev => ({ ...prev, [key]: null }));
    }
    setLoadingMonths(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [monthCache, loadingMonths, selectedResourceId]);

  // Initialize cache with current month data
  useEffect(() => {
    const key = getCacheKey(currentDate.getFullYear(), currentDate.getMonth());
    if (monthData && !monthCache[key]) {
      setMonthCache(prev => ({ ...prev, [key]: monthData }));
    }
  }, [currentDate, monthData, monthCache]);

  // Preload adjacent months
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Load previous month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    loadMonth(prevYear, prevMonth);

    // Load next month
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    loadMonth(nextYear, nextMonth);
  }, [currentDate, loadMonth]);

  // Combine all cached month data into day groups
  const dayGroups = useMemo<DayGroup[]>(() => {
    const allEntries: CalendarTimeEntry[] = [];

    // Add entries from all cached months
    for (const data of Object.values(monthCache)) {
      if (data?.entries) {
        allEntries.push(...data.entries);
      }
    }

    // Also add current month data if not in cache yet
    if (monthData?.entries) {
      const key = getCacheKey(currentDate.getFullYear(), currentDate.getMonth());
      if (!monthCache[key]) {
        allEntries.push(...monthData.entries);
      }
    }

    // Filter entries
    const filtered = allEntries
      .filter(e => selectedCompanies.size === 0 ? false : selectedCompanies.has(normalizeCompanyName(e.companyName)))
      .filter(e => !searchFilter || searchFilter(e));

    // Group by date
    const groups = new Map<string, CalendarTimeEntry[]>();
    for (const entry of filtered) {
      const dateKey = entry.dateWorked;
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(entry);
    }

    // Convert to array and sort by date
    const result: DayGroup[] = [];
    for (const [dateStr, entries] of groups) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      result.push({
        date,
        dateStr,
        entries: entries.sort((a, b) => b.hoursWorked - a.hoursWorked),
        totalHours: getTotalHours(entries),
      });
    }

    return result.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [monthData, monthCache, currentDate, selectedCompanies, searchFilter]);

  // Update visible date range on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || dayGroups.length === 0) return;

    const updateVisibleRange = () => {
      const containerRect = container.getBoundingClientRect();
      const headers = container.querySelectorAll('[data-date]');

      let firstVisible: Date | null = null;
      let lastVisible: Date | null = null;

      headers.forEach((header) => {
        const rect = header.getBoundingClientRect();
        const isVisible = rect.top < containerRect.bottom && rect.bottom > containerRect.top;

        if (isVisible) {
          const dateStr = header.getAttribute('data-date');
          if (dateStr) {
            const [year, month, day] = dateStr.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            if (!firstVisible || date < firstVisible) firstVisible = date;
            if (!lastVisible || date > lastVisible) lastVisible = date;
          }
        }
      });

      setVisibleDateRange({ start: firstVisible, end: lastVisible });
    };

    updateVisibleRange();
    container.addEventListener('scroll', updateVisibleRange);
    return () => container.removeEventListener('scroll', updateVisibleRange);
  }, [dayGroups]);

  // Track previous currentDate to detect navigation
  const prevDateRef = useRef<string>(formatDateKey(currentDate));
  const hasScrolledInitially = useRef(false);

  // Scroll to a target date in the list
  const scrollToDate = useCallback((targetKey: string, exact: boolean) => {
    const container = scrollContainerRef.current;
    if (!container || dayGroups.length === 0) return;

    let target = container.querySelector(`[data-date="${targetKey}"]`);
    if (!target && !exact) {
      // Find nearest date >= target
      const headers = container.querySelectorAll('[data-date]');
      for (const header of headers) {
        const dateStr = header.getAttribute('data-date');
        if (dateStr && dateStr >= targetKey) {
          target = header;
          break;
        }
      }
    }
    if (target) {
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [dayGroups]);

  // Auto-scroll to today on initial mount
  useEffect(() => {
    if (hasScrolledInitially.current || dayGroups.length === 0) return;
    hasScrolledInitially.current = true;
    scrollToDate(formatDateKey(new Date()), false);
  }, [dayGroups, scrollToDate]);

  // Scroll to first of month when currentDate changes (user navigated)
  useEffect(() => {
    const newKey = formatDateKey(currentDate);
    if (prevDateRef.current !== newKey && dayGroups.length > 0) {
      prevDateRef.current = newKey;
      // Scroll to first day of the current month
      const monthStart = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`;
      scrollToDate(monthStart, false);
    }
  }, [currentDate, dayGroups, scrollToDate]);

  const today = new Date();
  const totalHours = dayGroups.reduce((sum, g) => sum + g.totalHours, 0);

  // Format the header date range
  const getHeaderDateRange = () => {
    if (!visibleDateRange.start || !visibleDateRange.end) {
      return `${shortMonthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }

    const start = visibleDateRange.start;
    const end = visibleDateRange.end;

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${shortMonthNames[start.getMonth()]} ${start.getFullYear()}`;
    }

    if (start.getFullYear() === end.getFullYear()) {
      return `${shortMonthNames[start.getMonth()]} - ${shortMonthNames[end.getMonth()]} ${start.getFullYear()}`;
    }

    return `${shortMonthNames[start.getMonth()]} ${start.getFullYear()} - ${shortMonthNames[end.getMonth()]} ${end.getFullYear()}`;
  };

  if (dayGroups.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full text-slate-500 cursor-pointer"
        onClick={() => onAddEntry(currentDate)}
      >
        <svg className="w-12 h-12 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-sm">No entries found</p>
        <p className="mt-2 text-xs text-slate-600">Click anywhere to add an entry</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Schedule Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700 bg-slate-800/50">
        <div className="text-sm font-medium text-white">
          {getHeaderDateRange()}
        </div>
        <div className="text-xs text-slate-400">
          {dayGroups.length} {dayGroups.length === 1 ? 'day' : 'days'} · {formatHours(totalHours)}
        </div>
      </div>

      {/* Scrollable List */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {dayGroups.map((group) => {
          const isToday = group.date.toDateString() === today.toDateString();

          return (
            <div key={group.dateStr} className="border-b border-slate-700/30">
              {/* Date Header (Sticky) */}
              <div
                data-date={group.dateStr}
                className={`sticky top-0 z-10 px-4 py-1.5 flex items-center justify-between ${
                  isToday ? 'bg-blue-900/30' : 'bg-slate-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isToday ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'
                  }`}>
                    {group.date.getDate()}
                  </div>
                  <span className={`text-sm ${isToday ? 'text-blue-400 font-medium' : 'text-slate-300'}`}>
                    {formatShortDate(group.date)}
                  </span>
                </div>
                <span className="text-xs text-slate-500">{formatHours(group.totalHours)}</span>
              </div>

              {/* Entries for this day - click blank space to add entry */}
              <div
                className="px-4 py-1.5 space-y-1 cursor-pointer"
                onClick={() => onAddEntry(group.date)}
              >
                {group.entries.map((entry, index) => {
                  const color = getCompanyColor(entry.companyName);
                  return (
                    <button
                      key={entry.id || index}
                      onClick={(e) => { e.stopPropagation(); onEntryClick(entry, e); }}
                      className="w-full text-left px-3 py-2 rounded-md bg-slate-700/40 hover:bg-slate-700/70 border-l-2 transition-colors"
                      style={{ borderLeftColor: color.hex }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-300 truncate">{entry.companyName}</div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">{entry.summaryNotes}</div>
                        </div>
                        <div className="text-sm font-medium text-slate-400 flex-shrink-0">
                          {formatHours(entry.hoursWorked)}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Add Entry for this day */}
                <button
                  onClick={(e) => { e.stopPropagation(); onAddEntry(group.date); }}
                  className="w-full py-1.5 text-slate-600 hover:text-blue-400 hover:bg-slate-700/30 rounded transition-colors flex items-center justify-center gap-1 text-xs"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
